import type { ItemNeed } from './grouping';

/**
 * How a pay/sign control looks, in one table.
 *
 * Two surfaces render the same choice: `ActionButton`, for the row action that
 * opens a dialog, and `ItemCard`, for the button that opens the guided flow on
 * one item. They were styled independently, so a card whose badge marked it
 * `pay` could sit beside a button styled as though it were a signature.
 *
 * The tone matches `NeedBadge` deliberately. The badge on the left of a card
 * and the button on its right describe the same obligation, and teaching two
 * colour codes in one row teaches neither. Money takes green, a signature
 * amber, and both wear the same soft fill at the same weight — so hue and icon
 * are what separate them, not prominence.
 *
 * Green is also the app's "settled" hue (the Paid tag, the sealed checkmark),
 * so on a row this button can sit beside a green tag meaning the opposite
 * thing — and now in the very same green, since this button borrows the tag
 * ramp. That overlap is deliberate and was accepted: the icon is what says
 * which, and a pill with a card icon is not read as a status chip.
 *
 * Colour never carries this alone (§11) — which is what makes the overlap
 * above survivable: the icon is the part that survives greyscale, and it comes
 * from `actionIconOf` so a button and its menu item can never disagree.
 */
export interface ActionAppearance {
  variant: 'contained' | 'text';
  color: 'success' | 'primary' | 'warning';
  /** Tailwind for the fill and the edge. */
  className: string;
}

/**
 * Both treatments carry an edge, so the two buttons are one object in two hues
 * rather than two kinds of control. `inset-ring` rather than `border`, so the
 * edge is drawn inside the box and the two buttons keep identical heights — a
 * real border would add a pixel to one and misalign it against the amount
 * beside it.
 */
const PAY: ActionAppearance = {
  // The soft fill sign wears, but drawn from the STATUS-TAG green rather than
  // the muted palette one. `success/12` over white lands on #e4f0ea — a warm
  // grey with the green almost tinted out of it, which is the same reason the
  // status tags do not use that ramp either. The tag green keeps its colour at
  // this weight, and its ink clears 4.6:1 on its own fill.
  variant: 'text',
  color: 'success',
  className:
    'bg-tag-success hover:bg-tag-success-edge text-tag-success-ink hover:text-tag-success-ink-strong inset-ring inset-ring-tag-success-edge',
};

const SIGN: ActionAppearance = {
  // A SOFT FILL rather than an outline-only button: the same pill and the same
  // weight as pay, separated by hue and icon. On the tag ramp too, so the pair
  // sitting side by side are two hues of one control rather than one lively
  // button beside a washed-out one.
  variant: 'text',
  color: 'warning',
  className:
    'bg-tag-warning hover:bg-tag-warning-edge text-tag-warning-ink hover:text-tag-warning-ink-strong inset-ring inset-ring-tag-warning-edge',
};

/**
 * The needs that produce a control. `waiting` is absent on purpose — it is the
 * one state asking nothing of this director, so it renders no button at all;
 * giving it an appearance here would invent a control that must never exist.
 */
export type ActionableNeed = Exclude<ItemNeed, 'waiting'>;

/**
 * `signAndPay` wears the pay treatment: it is money, and its badge is already
 * the money badge. The flow it opens starts on the signature, so the caller
 * pairs this with the pen icon — the icon says which screen comes first, the
 * colour says what is ultimately at stake.
 */
const BY_NEED: Record<ActionableNeed, ActionAppearance> = {
  pay: PAY,
  signAndPay: PAY,
  sign: SIGN,
};

export const appearanceOfNeed = (need: ActionableNeed): ActionAppearance => BY_NEED[need];

/** The pill shape shared with `OutstandingHero`. "Get it done" and a row's own
 *  action start the SAME flow on the same work, so a row button that looked
 *  like a different species of control would suggest it did a different thing.
 *  `shadow-none` on both states because a contained MUI button lifts on hover
 *  by default, and these deliberately sit flat. */
export const ACTION_SHAPE = 'rounded-full font-semibold shadow-none hover:shadow-none';
